/**
 * One row of the saved/seen backlog: org, title, score, date, the same
 * interested/not-interested actions `MatchCard` exposes (PIVOT-127), and the
 * candidate's own application-status control + "did you hear back?" nudge
 * (PIVOT-112) — this is the one page a saved match stays on after the day it
 * was scored, so it is where the application-tracking log lives.
 *
 * `SavedSeen.tsx`'s list is a backlog someone is scanning, not fresh matches
 * being evaluated one at a time, so it gets the repo's closest equivalent to
 * Stripe's transactions table — a dense row, not a card
 * (2026-08-07-plan-frontend-redesign.md §3c). Reuses `ScoreBadges` verbatim
 * for the score column rather than inventing a second way to draw a
 * score: PIVOT-091 already settled that a 0-100 number reads as a school
 * grade and that the three axes never collapse into one headline figure
 * (constitution §I) — a dense list is not a reason to relitigate either.
 *
 * PIVOT-133: `statusPending` disables the application-status `<select>` for
 * the round trip of its own write. That's the whole race fix — a disabled
 * control cannot start a second PATCH before the first settles, so there is
 * no second request to arrive out of order. `interestFailed`/`statusFailed`/
 * `outcomeFailed` are flags the caller (`SavedSeen.tsx`) sets after reverting
 * its own optimistic update; this component only renders the reason, near
 * the control that failed, never a page banner.
 */

import { useState } from 'react'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import { isHttpUrl } from '../lib/url'
import type { ApplicationStatus, Interest, Locale, Match } from '../types/domain'
import { Icon } from './Icon'
import { ScoreBadges } from './ScoreBadges'
import './MatchListRow.css'
import '../styles/job-posting-link.css'

const STATUS_OPTIONS: { value: ApplicationStatus; labelKey: TranslationKey }[] = [
  { value: 'none', labelKey: 'savedSeen.statusNone' },
  { value: 'applied', labelKey: 'savedSeen.statusApplied' },
  { value: 'responded', labelKey: 'savedSeen.statusResponded' },
  { value: 'interview', labelKey: 'savedSeen.statusInterview' },
  { value: 'offer', labelKey: 'savedSeen.statusOffer' },
]

/**
 * Short, column-friendly date: `scored_at` is the only per-match timestamp
 * `Match` carries (`job.first_seen`/`last_seen` belong to the posting, not
 * this user's match on it) — see `types/domain.ts`. Deliberately shorter
 * than `CvUpload`'s `formatUploadDate` (numeric day + short month rather than
 * "1 January 2026"): that one sits alone in a sentence, this one repeats down
 * a column of rows and a long form would be the first thing to wrap.
 */
function formatScoredDate(iso: string, locale: Locale): string {
  const parsed = new Date(iso)
  // An unparseable date is not worth blocking the row over; show it raw.
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

interface MatchListRowProps {
  match: Match
  onInterest: (jobId: string, interest: Interest) => void | Promise<void>
  /** Renders the application-status <select> (PIVOT-112). */
  onApplicationStatus: (jobId: string, status: ApplicationStatus) => void | Promise<void>
  /** Renders the "did you hear back?" banner when `match.outcome_due` is true. */
  onOutcome: (jobId: string, heardBack: boolean) => void | Promise<void>
  /** "This job no longer exists" (PIVOT-166) — see `MatchCard`'s identical prop. */
  onReportGone: (jobId: string) => void | Promise<void>
  /** PIVOT-133: the caller already reverted its own optimistic update for
   * this job; these only say why, near the control that failed. Flags, not
   * messages, per frontend/CLAUDE.md §7 — `t('common.error')` at render. */
  interestFailed?: boolean
  statusFailed?: boolean
  outcomeFailed?: boolean
  /** See `MatchCard`'s identical props. */
  reportedGone?: boolean
  reportGoneFailed?: boolean
}

export function MatchListRow({
  match,
  onInterest,
  onApplicationStatus,
  onOutcome,
  onReportGone,
  interestFailed = false,
  statusFailed = false,
  outcomeFailed = false,
  reportedGone = false,
  reportGoneFailed = false,
}: MatchListRowProps) {
  const { locale, t } = useI18n()
  const [pending, setPending] = useState<Interest | null>(null)
  const [outcomePending, setOutcomePending] = useState(false)
  // PIVOT-133: the whole race fix for the status select — disabled while
  // its own write is in flight, so a second change cannot start before the
  // first settles and there is never a second PATCH to arrive out of order.
  const [statusPending, setStatusPending] = useState(false)
  const [reportingGone, setReportingGone] = useState(false)

  const handleInterest = (interest: Interest) => {
    setPending(interest)
    void Promise.resolve(onInterest(match.job.id, interest)).finally(() => setPending(null))
  }

  const handleReportGone = () => {
    setReportingGone(true)
    void Promise.resolve(onReportGone(match.job.id)).finally(() => setReportingGone(false))
  }

  const handleApplicationStatus = (status: ApplicationStatus) => {
    setStatusPending(true)
    void Promise.resolve(onApplicationStatus(match.job.id, status)).finally(() =>
      setStatusPending(false),
    )
  }

  const handleOutcome = (heardBack: boolean) => {
    setOutcomePending(true)
    void Promise.resolve(onOutcome(match.job.id, heardBack)).finally(() => setOutcomePending(false))
  }

  // PIVOT-141: same reasoning as `MatchCard`'s `rejected` — a "no" gets its
  // own muted look instead of sharing the pine pill "yes" gets.
  const rejected = match.interest === 'not_interested'

  return (
    <li className={`match-list-row tag-${match.tag}${rejected ? ' match-list-row--rejected' : ''}`}>
      <div className="match-list-row-main">
        <span className="match-list-row-tag">
          {match.tag === 'direct' ? t('dashboard.tagDirect') : t('dashboard.tagLateral')}
        </span>
        <h3 className="match-list-row-title">{match.job.title}</h3>
        <p className="match-list-row-org">
          {match.job.org} ·{' '}
          {match.job.location?.trim() || t('dashboard.locationNotStated')}
        </p>
        {/* PIVOT-164: same note as `MatchCard` -- `job` is always the
            canonical, this names whichever other board(s) also carry it. */}
        {match.also_on.length > 0 && (
          <p className="match-list-row-also-on">
            {t('dashboard.alsoOn', { sources: match.also_on.join(', ') })}
          </p>
        )}
      </div>

      <div className="match-list-row-score">
        <ScoreBadges
          expScore={match.exp_score}
          valuesScore={match.values_score}
          salaryScore={match.salary_score}
        />
      </div>

      <p className="match-list-row-date">{formatScoredDate(match.scored_at, locale)}</p>

      {/* PIVOT-141: moved to sit right after the date rather than fighting
          the view/interest actions for space on its own full-width line —
          `flex: 0 0 auto` (MatchListRow.css) sizes it to its content instead
          of stretching, so it stops rendering as a 1129px grey bar. */}
      <div className="select-shell match-list-row-select">
        <select
          className="match-list-row-application-select"
          aria-label={t('savedSeen.applicationStatusLabel')}
          value={match.application_status}
          // Encodes the funnel stage the way `ScoreBadges` encodes a band —
          // none/applied/responded+interview/offer each get their own
          // reading (MatchListRow.css), so "Applied", "Interview" and
          // "Offer" stop rendering as three identical grey bars.
          data-status={match.application_status}
          // PIVOT-133: disabled for the round trip of its own write — the
          // race this control used to allow (two PATCHes in flight, last
          // response wins regardless of click order) cannot happen if a
          // second change is physically unable to start before the first
          // settles.
          disabled={statusPending}
          onChange={(event) => handleApplicationStatus(event.target.value as ApplicationStatus)}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
        <Icon name="chevron" />
      </div>
      {statusFailed && <p className="form-error">{t('common.error')}</p>}

      <div className="match-list-row-actions">
        {isHttpUrl(match.job.url) ? (
          <a href={match.job.url} target="_blank" rel="noreferrer" className="job-posting-link">
            {t('dashboard.viewPosting')}
            <Icon name="arrow-out" />
          </a>
        ) : (
          // Same guard, same fallback shape as MatchCard (PIVOT-132): no <a>
          // at all, so there is nothing to click and nothing for a
          // `javascript:`/`data:` scheme to execute. /saved is specifically
          // the page that surfaces *old* score rows — exactly the pre-guard
          // corpus PIVOT-114's defense-in-depth exists for.
          <span className="job-posting-link">{match.job.title}</span>
        )}
        <button
          type="button"
          className={`btn match-list-row-btn ${match.interest === 'interested' ? 'btn-primary' : 'btn-outline'}`}
          disabled={pending !== null}
          onClick={() => handleInterest('interested')}
        >
          <Icon name="check" />
          {match.interest === 'interested'
            ? t('dashboard.interestedMarked')
            : t('dashboard.interested')}
        </button>
        {/* PIVOT-141: `btn-settled` (styles/primitives.css) instead of the
            same `btn-primary` pine fill "interested" gets — see MatchCard's
            identical change for why. */}
        <button
          type="button"
          className={`btn match-list-row-btn ${match.interest === 'not_interested' ? 'btn-settled' : 'btn-outline'}`}
          disabled={pending !== null}
          onClick={() => handleInterest('not_interested')}
        >
          <Icon name="cross" />
          {match.interest === 'not_interested'
            ? t('dashboard.notInterestedMarked')
            : t('dashboard.notInterested')}
        </button>
      </div>
      {interestFailed && <p className="form-error">{t('common.error')}</p>}

      {match.outcome_due && (
        // Announced because it appears from server state on load, not from a
        // click this candidate just made (PIVOT-134) — the only signal a
        // screen reader would otherwise have that this row is asking them
        // something.
        <div className="match-list-row-outcome" role="status">
          <p className="match-list-row-outcome-prompt">
            <Icon name="alert" />
            {t('savedSeen.outcomePrompt')}
          </p>
          <div className="match-list-row-outcome-actions">
            <button
              type="button"
              className="btn btn-outline"
              disabled={outcomePending}
              onClick={() => handleOutcome(true)}
            >
              {t('savedSeen.outcomeYes')}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              disabled={outcomePending}
              onClick={() => handleOutcome(false)}
            >
              {t('savedSeen.outcomeNotYet')}
            </button>
          </div>
          {outcomeFailed && <p className="form-error">{t('common.error')}</p>}
        </div>
      )}
      {/* PIVOT-166: quiet and last, same reasoning as `MatchCard`'s
          identical control. */}
      <button
        type="button"
        className="btn btn-ghost match-list-row-report-gone"
        disabled={reportedGone || reportingGone}
        aria-busy={reportingGone}
        onClick={handleReportGone}
      >
        {t(reportedGone ? 'dashboard.reportGoneSent' : 'dashboard.reportGone')}
      </button>
      {reportGoneFailed && <p className="form-error">{t('common.error')}</p>}
    </li>
  )
}
